import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSecondaryAccounts,
  addSecondaryAccount,
  deleteSecondaryAccount,
  updateSecondaryAccountRole,
  clearError,
} from "../reducers/secondaryAccountSlice";

const SecondaryAccountFrame = () => {
  const dispatch = useDispatch();
  const { accounts, loading, error } = useSelector(
    (state) => state.secondaryAccount,
  );
  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  // Form state
  const [email, setEmail] = useState("");
  const [fullname, setFullname] = useState("");
  const [role, setRole] = useState("Display");
  const [localError, setLocalError] = useState("");

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [editRole, setEditRole] = useState("");

  useEffect(() => {
    if (license_key) {
      dispatch(fetchSecondaryAccounts({ license_key }));
    }
  }, [dispatch, license_key]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const validateEmail = (email) => {
    return /^[\w.+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  };

  const handleAdd = () => {
    if (!validateEmail(email)) {
      setLocalError("Enter a valid email address.");
      return;
    }
    if (!fullname.trim()) {
      setLocalError("Enter user full name.");
      return;
    }
    if (accounts.some((acc) => acc.email === email)) {
      setLocalError("An account with this email already exists.");
      return;
    }

    setLocalError("");
    dispatch(addSecondaryAccount({ email, fullname, role, license_key }))
      .unwrap()
      .then(() => {
        // Clear form on success
        setEmail("");
        setFullname("");
        setRole("Display");
      })
      .catch(() => {
        // Error is handled by reducer
      });
  };

  const handleDelete = (accountEmail) => {
    if (window.confirm(`Are you sure you want to delete ${accountEmail}?`)) {
      dispatch(deleteSecondaryAccount({ email: accountEmail, license_key }));
    }
  };

  const handleEditOpen = (account) => {
    setEditingAccount(account);
    setEditRole(account.role);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingAccount(null);
    setEditRole("");
  };

  const handleEditSave = () => {
    if (editingAccount && editRole !== editingAccount.role) {
      dispatch(
        updateSecondaryAccountRole({
          email: editingAccount.email,
          role: editRole,
          license_key,
        }),
      )
        .unwrap()
        .then(() => {
          handleEditClose();
        })
        .catch(() => {
          // Error is handled by reducer
        });
    } else {
      handleEditClose();
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, maxWidth: 900 }}>
      <Typography variant="h6" gutterBottom>
        Secondary Accounts Management
      </Typography>

      {loading && <CircularProgress size={24} sx={{ mb: 2 }} />}

      {(error || localError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {localError || error}
        </Alert>
      )}

      {/* Add New Account Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Add New Secondary Account
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
          />
          <TextField
            label="Full Name"
            value={fullname}
            onChange={(e) => setFullname(e.target.value)}
            size="small"
          />
          <FormControl size="small">
            <InputLabel>Role</InputLabel>
            <Select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              label="Role"
            >
              <MenuItem value="Display">Display</MenuItem>
              <MenuItem value="Change">Change</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="success"
            onClick={handleAdd}
            disabled={loading}
          >
            Add Account
          </Button>
        </Box>
      </Box>

      {/* Accounts List Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Current Secondary Accounts ({accounts.length})
        </Typography>
        {accounts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No secondary accounts added yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.email}>
                    <TableCell>{account.email}</TableCell>
                    <TableCell>{account.fullname}</TableCell>
                    <TableCell>
                      <Chip
                        label={account.role}
                        color={
                          account.role === "Change" ? "primary" : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditOpen(account)}
                        disabled={loading}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(account.email)}
                        disabled={loading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Audit Button */}

      {/* Edit Role Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Account Role</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Email:</strong> {editingAccount?.email}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Name:</strong> {editingAccount?.fullname}
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                label="Role"
              >
                <MenuItem value="Display">Display</MenuItem>
                <MenuItem value="Change">Change</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SecondaryAccountFrame;
